import * as React from 'react';

import { brandIcons } from '../../assets/brandIcons';
import googleIcon from '../../assets/images/google-icon.svg';
import { cn } from '../../lib/utils';

export type SocialProvider = 'google' | 'microsoft' | 'apple';

export interface SocialLoginButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The social provider for this button */
  provider: SocialProvider;
  /** Custom label text (default: "Log in with {Provider}") */
  label?: string;
  /** Whether the button is in loading state */
  loading?: boolean;
}

const providerConfig: Record<
  SocialProvider,
  { icon: string; defaultLabel: string; iconSize: { width: number; height: number } }
> = {
  google: {
    icon: googleIcon,
    defaultLabel: 'Sign in with Google',
    iconSize: { width: 19, height: 18 },
  },
  // Entra-federated tenants (TDEV-156): the shared brand asset, labelled the way
  // the wizard's sign-in tile is so the customer sees one product name end to end.
  microsoft: {
    icon: brandIcons['entra-id'],
    defaultLabel: 'Sign in with Microsoft Entra ID',
    iconSize: { width: 18, height: 18 },
  },
  apple: {
    icon: googleIcon, // Placeholder - would use Apple icon
    defaultLabel: 'Log in with Apple',
    iconSize: { width: 19, height: 18 },
  },
};

/**
 * SocialLoginButton Molecule
 *
 * A button for OAuth social login providers:
 * - Provider icon + label text
 * - White background with grey border
 * - Configurable provider (Google, Microsoft Entra ID, Apple)
 * - `data-testid="social-login-<provider>"` for e2e selection
 *
 * @example
 * ```tsx
 * <SocialLoginButton provider="google" onClick={handleGoogleLogin} />
 * ```
 */
export const SocialLoginButton = React.forwardRef<HTMLButtonElement, SocialLoginButtonProps>(
  ({ provider, label, loading, className, disabled, ...props }, ref) => {
    const config = providerConfig[provider];
    const displayLabel = label || config.defaultLabel;

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        data-testid={`social-login-${provider}`}
        className={cn(
          'w-full h-10 bg-dawn-white border border-solid border-grey-200 rounded',
          'flex items-center justify-center gap-3',
          'font-outfit font-normal text-[13px] leading-[1.3] text-grey-400',
          'hover:border-grey-400 transition-colors duration-200',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className
        )}
        {...props}
      >
        {loading ? (
          <span className="animate-spin w-[18px] h-[18px] border-2 border-grey-400 border-t-transparent rounded-full" />
        ) : (
          <img
            src={config.icon}
            alt={provider}
            style={{
              width: `${config.iconSize.width}px`,
              height: `${config.iconSize.height}px`,
            }}
          />
        )}
        <span>{displayLabel}</span>
      </button>
    );
  }
);

SocialLoginButton.displayName = 'SocialLoginButton';

export default SocialLoginButton;
